import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Trash2, Search, Filter, Inbox, Mail, Phone, MapPin, GraduationCap, Link2, FileText, Save, TrendingUp, Calendar, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

type AppStatus = "new" | "shortlisted" | "rejected" | "hired";

type Application = {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  city: string | null;
  education: string | null;
  why_interested: string;
  portfolio_links: string | null;
  status: AppStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
};

const statusBadge = (status: AppStatus) => {
  switch (status) {
    case "new":
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">New</Badge>;
    case "shortlisted":
      return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Shortlisted</Badge>;
    case "rejected":
      return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
    case "hired":
      return <Badge className="bg-violet-100 text-violet-800 border-violet-200">Hired</Badge>;
  }
};

export const ApplicationsPanel = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [openApp, setOpenApp] = useState<Application | null>(null);
  const [notesDraft, setNotesDraft] = useState("");

  const { data: applications = [], isLoading } = useQuery({
    queryKey: ["admin-applications", statusFilter],
    queryFn: async () => {
      let q = supabase.from("intern_applications").select("*").order("created_at", { ascending: false });
      if (statusFilter !== "all") q = q.eq("status", statusFilter as AppStatus);
      const { data, error } = await q;
      if (error) throw error;
      return data as Application[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Application> }) => {
      const { error } = await supabase.from("intern_applications").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Updated.");
      queryClient.invalidateQueries({ queryKey: ["admin-applications"] });
    },
    onError: () => toast.error("Update failed."),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("intern_applications").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Application deleted.");
      queryClient.invalidateQueries({ queryKey: ["admin-applications"] });
      setOpenApp(null);
    },
    onError: () => toast.error("Delete failed."),
  });

  const filtered = applications.filter((a) => {
    const q = searchQuery.toLowerCase();
    return (
      a.full_name.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q) ||
      a.phone.toLowerCase().includes(q) ||
      (a.city || "").toLowerCase().includes(q)
    );
  });

  const newCount = applications.filter((a) => a.status === "new").length;

  // Stats: weekly / monthly counts of new submissions and status changes
  const now = Date.now();
  const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
  const monthAgo = now - 30 * 24 * 60 * 60 * 1000;

  const inRange = (iso: string, since: number) => new Date(iso).getTime() >= since;

  // "Status change" = updated_at meaningfully later than created_at (>60s gap)
  const isStatusChanged = (a: Application) =>
    new Date(a.updated_at).getTime() - new Date(a.created_at).getTime() > 60_000;

  const stats = {
    newWeek: applications.filter((a) => inRange(a.created_at, weekAgo)).length,
    newMonth: applications.filter((a) => inRange(a.created_at, monthAgo)).length,
    changedWeek: applications.filter((a) => isStatusChanged(a) && inRange(a.updated_at, weekAgo)).length,
    changedMonth: applications.filter((a) => isStatusChanged(a) && inRange(a.updated_at, monthAgo)).length,
    shortlistedMonth: applications.filter((a) => a.status === "shortlisted" && inRange(a.updated_at, monthAgo)).length,
    hiredMonth: applications.filter((a) => a.status === "hired" && inRange(a.updated_at, monthAgo)).length,
  };

  const exportCsv = () => {
    const rows = [
      ["Date", "Name", "Email", "Phone", "City", "Education", "Status", "Why interested", "Portfolio", "Notes"],
      ...filtered.map((a) => [
        new Date(a.created_at).toISOString(),
        a.full_name,
        a.email,
        a.phone,
        a.city || "",
        a.education || "",
        a.status,
        a.why_interested.replace(/\n/g, " "),
        a.portfolio_links || "",
        a.admin_notes || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `intern-applications-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Stats widget */}
      <div className="mb-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <Calendar className="h-3.5 w-3.5" /> New · 7d
          </div>
          <div className="mt-1 text-2xl font-bold text-foreground">{stats.newWeek}</div>
          <div className="text-xs text-muted-foreground">{stats.newMonth} in last 30d</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <RefreshCw className="h-3.5 w-3.5" /> Status changes · 7d
          </div>
          <div className="mt-1 text-2xl font-bold text-foreground">{stats.changedWeek}</div>
          <div className="text-xs text-muted-foreground">{stats.changedMonth} in last 30d</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <TrendingUp className="h-3.5 w-3.5" /> Shortlisted · 30d
          </div>
          <div className="mt-1 text-2xl font-bold text-emerald-700">{stats.shortlistedMonth}</div>
          <div className="text-xs text-muted-foreground">moved to shortlist</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <CheckCircle2 className="h-3.5 w-3.5" /> Hired · 30d
          </div>
          <div className="mt-1 text-2xl font-bold text-violet-700">{stats.hiredMonth}</div>
          <div className="text-xs text-muted-foreground">final outcomes</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {applications.length} total · <span className="font-semibold text-foreground">{newCount} new</span>
        </p>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!filtered.length}>
          Export CSV
        </Button>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, phone, city…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="shortlisted">Shortlisted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="hired">Hired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Inbox className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No applications</p>
          <p className="text-sm">Nothing matches your filters yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((a, i) => (
            <motion.button
              type="button"
              key={a.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              onClick={() => {
                setOpenApp(a);
                setNotesDraft(a.admin_notes || "");
              }}
              className="w-full text-left p-5 rounded-xl bg-card border border-border hover:border-primary/40 hover:shadow-card transition-all"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {statusBadge(a.status)}
                    <span className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground">{a.full_name}</h3>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>
                    <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{a.phone}</span>
                    {a.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.city}</span>}
                    {a.education && <span className="inline-flex items-center gap-1"><GraduationCap className="h-3 w-3" />{a.education}</span>}
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      <Dialog open={!!openApp} onOpenChange={(o) => !o && setOpenApp(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {openApp && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 flex-wrap">
                  {openApp.full_name}
                  {statusBadge(openApp.status)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <a href={`mailto:${openApp.email}`} className="inline-flex items-center gap-2 text-foreground hover:text-primary">
                    <Mail className="h-4 w-4" />{openApp.email}
                  </a>
                  <a href={`tel:${openApp.phone}`} className="inline-flex items-center gap-2 text-foreground hover:text-primary">
                    <Phone className="h-4 w-4" />{openApp.phone}
                  </a>
                  {openApp.city && (
                    <div className="inline-flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />{openApp.city}
                    </div>
                  )}
                  {openApp.education && (
                    <div className="inline-flex items-center gap-2 text-muted-foreground">
                      <GraduationCap className="h-4 w-4" />{openApp.education}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> Why interested
                  </h4>
                  <p className="text-sm text-foreground whitespace-pre-wrap bg-muted/50 rounded-lg p-3">
                    {openApp.why_interested}
                  </p>
                </div>

                {openApp.portfolio_links && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Link2 className="h-3.5 w-3.5" /> Portfolio / Links
                    </h4>
                    <p className="text-sm text-foreground whitespace-pre-wrap bg-muted/50 rounded-lg p-3 break-all">
                      {openApp.portfolio_links}
                    </p>
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Status
                  </h4>
                  <Select
                    value={openApp.status}
                    onValueChange={(v) => {
                      updateMutation.mutate({ id: openApp.id, patch: { status: v as AppStatus } });
                      setOpenApp({ ...openApp, status: v as AppStatus });
                    }}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="shortlisted">Shortlisted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="hired">Hired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Admin notes
                  </h4>
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder="Internal notes — only visible to admins"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 gap-1.5"
                    onClick={() => updateMutation.mutate({ id: openApp.id, patch: { admin_notes: notesDraft } })}
                    disabled={updateMutation.isPending}
                  >
                    <Save className="h-4 w-4" /> Save notes
                  </Button>
                </div>

                <div className="pt-3 border-t border-border flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    Submitted {new Date(openApp.created_at).toLocaleString("en-IN")}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (confirm("Delete this application permanently?")) {
                        deleteMutation.mutate(openApp.id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};